import { Loader2Icon } from 'lucide-react';
import React from 'react';
import styled, { css, keyframes } from 'styled-components';

import { InputOTP, InputOTPGroup, InputOTPSlot } from '@column/ui';

import { CompletionCheck } from './CompletionCheck';

export interface CodeInputStyleProps {
  isDisabled: boolean;
  isLoading: boolean;
  // When true, blur the segments and draw an animated success check over them,
  // in place of the loading spinner. Mirrors the passkey login button's confirmation.
  isComplete: boolean;
}

export interface CodeInputProps extends Partial<CodeInputStyleProps> {
  onChange?: (value: string) => void;
  fields?: number;
  className?: string;
  type?: 'text' | 'password' | 'number';
  value?: string;
  placeholder?: string;
  hideCaret?: boolean;
  showPlaceholderOnFocus?: boolean;
  // When set, exposes the field to password managers / OS autofill (e.g. `one-time-code`
  // lets 1Password, Safari and Chrome auto-fill a 2FA code).
  autoComplete?: 'one-time-code';
  name?: string;
  // Stretch the segments to fill the available width instead of capping at their
  // natural size (6 × 44px = 264px). Useful in wider dialogs where a centered,
  // fixed-width field looks lost.
  fullWidth?: boolean;
}

const CodeInputWrapper = styled.div<{ $fullWidth: boolean }>`
  position: relative;
  display: flex;
  justify-content: center;
  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      width: 100%;
    `}
`;

const FieldsWrapper = styled.div<{ $isLoading: boolean; $fullWidth: boolean }>`
  display: flex;
  justify-content: center;
  transition: filter 0.2s;

  /* The @column/ui OTP group ships a flat 1px inset border. Override it with the
     ui-kit text-input shadow (inset border + "depressed" inner shadow) so the grouped
     field matches the standard text inputs it sits alongside in these dialogs, and swap
     its bg-gray-25 fill for the standard input field tint. */
  [data-slot='input-otp-group'] {
    background-color: #575a640a;
    box-shadow:
      inset 0 0 0 1px ${({ theme }) => theme.style.inputDefaultBorderColor},
      ${({ theme }) => theme.style.depressedShadow};
    transition: box-shadow 0.2s;
  }

  &:hover [data-slot='input-otp-group'] {
    box-shadow:
      inset 0 0 0 1px ${({ theme }) => theme.style.inputDefaultHoveredBorderColor},
      ${({ theme }) => theme.style.depressedShadow};
  }

  /* Widen the segments so the whole field is roomier. Password-manager / OS autofill
     affordances (e.g. 1Password's inline icon) anchor to the input's right edge, and a
     wider field keeps them from crowding the last digit / spilling past the group.
     Force 40px tall (the @column/ui slot ships h-9 = 36px + my-px margins = 38px) so the
     field matches the height of the standard ui-kit text inputs beside it. */
  [data-slot='input-otp-slot'] {
    width: 44px;
    height: 40px;
    margin-top: 0;
    margin-bottom: 0;
  }

  /* Focused-segment highlight: a 1px solid border with a larger semi-opaque ring around it.
     The border is an inset box-shadow so the field's hard edge stays within 40px; the ring
     is an outline (not an outset box-shadow) so it renders an even width on all four sides —
     an outset shadow gets stacked under/over neighbouring segments unevenly, making the ring
     look thicker on the right. box-shadow needs !important (Tailwind composes it from a
     universal rule); the outline overrides the slot's outline-none utility. */
  [data-slot='input-otp-slot'][data-active='true'] {
    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.primary.background} !important;
    outline: 2px solid ${({ theme }) => `color-mix(in srgb, ${theme.primary.background} 30%, transparent)`} !important;
    outline-offset: 0;
  }

  /* The dividers between segments are a full-height right border that butts into the
     group's inset border at the top/bottom corners, which reads as a rough overlap. Drop
     the border entirely (redrawn as a pseudo-element below) — leaving even a transparent
     1px border-right also makes the border-box 1px wider on the right, which pushes the
     focus outline 1px further out on that side than the others. */
  [data-slot='input-otp-slot'] {
    border: 0;
    position: relative;
    /* @column/ui's outline-none leaves outline-style: none with outline-color defaulting to
       the (black) text color. Because the slot has transition-all, focusing animates the
       outline colour from black to the focus blue — a visible black flash. Give it a stable
       transparent solid outline so the transition starts from transparent instead. */
    outline: 2px solid transparent;
    outline-offset: 0;
  }

  [data-slot='input-otp-slot']:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 1px;
    bottom: 1px;
    right: -1px;
    width: 1px;
    background-color: ${({ theme }) => theme.style.inputDefaultBorderColor};
  }

  /* Hide the active segment's own right divider while focused: pseudo-elements paint above
     box-shadow, so it would otherwise punch a gap through the focus ring on the right edge
     (the left neighbour's divider sits under the z-10 active slot and stays covered), making
     the ring look thicker/uneven on the right. The focus ring delineates the cell instead. */
  [data-slot='input-otp-slot'][data-active='true']::after {
    display: none;
  }

  /* input-otp widens the hidden input to calc(100% + 40px) (clipped back) when it detects
     a password-manager badge, so 1Password's icon anchors just past the last digit. Nudge
     that to 48px for a little more breathing room. Scoped to the pushed state via the calc()
     in the inline style, so it's a no-op when no PWM badge is present. */
  [data-input-otp][style*='calc('] {
    width: calc(100% + 48px) !important;
    clip-path: inset(0 48px 0 0) !important;
  }

  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      width: 100%;

      /* input-otp renders a flex container (data-input-otp-container) holding the
         group, but it has no intrinsic width and shrinks to the group's natural size.
         Stretch the container and group, then let each slot flex so the whole field
         fills its parent instead of capping at 6 x 44px. */
      [data-input-otp-container],
      [data-slot='input-otp-group'] {
        width: 100%;
      }

      [data-slot='input-otp-slot'] {
        flex: 1;
        width: auto;
        min-width: 0;
      }
    `}

  ${({ $isLoading }) =>
    $isLoading &&
    css`
      filter: blur(3px);
    `}
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Loading = styled(Loader2Icon)<{ $isLoading: boolean }>`
  color: currentColor;

  position: absolute;
  left: 50%;
  top: 50%;
  width: 20px;
  height: 20px;
  margin: -10px 0 0 -10px;
  animation: ${spin} 1s linear infinite;
  transition:
    transform 0.2s,
    opacity 0.2s;

  ${({ $isLoading }) =>
    !$isLoading &&
    css`
      opacity: 0;
      pointer-events: none;
      transform: translateY(-12px) scale(0.75);
    `}
`;

const SuccessCheck = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  width: 20px;
  height: 20px;
  margin: -10px 0 0 -10px;
  color: ${({ theme }) => theme.success.background};
  pointer-events: none;
`;

export const CodeInput: React.FC<CodeInputProps> = (props) => {
  const fieldNum = props.fields ?? 6;
  const isLoading = props.isLoading ?? false;
  const isComplete = props.isComplete ?? false;
  const isDisabled = props.isDisabled ?? false;
  const fullWidth = props.fullWidth ?? false;
  const isNumeric = props.type === 'number';

  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const wasLoading = React.useRef(isLoading);

  // While the code is being verified the field is disabled, which drops the underlying
  // <input>'s DOM focus. input-otp doesn't reliably fire its onBlur for a programmatic
  // disable, so its internal focus state stays stale: when a verify attempt fails and the
  // field is re-enabled (e.g. a wrong code), the first segment still paints the focus ring
  // but keystrokes go nowhere until the user clicks in. Re-focus the input on the
  // loading→idle edge so the user can simply keep typing. Skip it on the success path
  // (isComplete) where the field is intentionally blurred behind the checkmark.
  React.useEffect(() => {
    if (wasLoading.current && !isLoading && !isComplete && !isDisabled) {
      wrapperRef.current?.querySelector<HTMLInputElement>('input[data-input-otp]')?.focus();
    }
    wasLoading.current = isLoading;
  }, [isLoading, isComplete, isDisabled]);

  return (
    <CodeInputWrapper ref={wrapperRef} className={props.className} $fullWidth={fullWidth}>
      <FieldsWrapper $isLoading={isLoading || isComplete} $fullWidth={fullWidth}>
        <InputOTP
          maxLength={fieldNum}
          value={props.value ?? ''}
          onChange={props.onChange}
          disabled={isDisabled || isLoading}
          name={props.name}
          autoComplete={props.autoComplete}
          pattern={isNumeric ? '^[0-9]*$' : undefined}
          inputMode={isNumeric ? 'numeric' : 'text'}
        >
          <InputOTPGroup>
            {Array.from({ length: fieldNum }, (_, index) => (
              <InputOTPSlot key={index} index={index} />
            ))}
          </InputOTPGroup>
        </InputOTP>
      </FieldsWrapper>
      {isComplete ? (
        <SuccessCheck>
          <CompletionCheck />
        </SuccessCheck>
      ) : (
        <Loading $isLoading={isLoading} />
      )}
    </CodeInputWrapper>
  );
};
